import {AsyncComponent} from 'vue';
import {RouteConfig} from 'vue-router/types/router';

/**
 * Lehrkraft-Bereich (/my-teaching) and Bewerbungs-Bereich (/my-application), see SPEC-LEHRKRAFT-BEREICH.md.
 *
 * Access (routeAccess.ts → helpers/portalMode.ts): only the roles decide (guiAccess TEACHER_PORTAL / APPLICANT_PORTAL).
 * Teacher-only logins only open routes with meta.portal 'teacher' (+ public token pages), everything else redirects
 * silently to /my-teaching/today; office logins without TEACHER_PORTAL cannot open the Lehrkraft-Bereich.
 *
 * Add new views as children of the matching parent (meta is NOT inherited in vue-router 3, so always use teacherChild /
 * applicantChild):
 * - portalTab: active tab of the navigation (components/teacherPortal/portalNav.ts)
 * - portalNoNav: form page without bottom navigation (own action area at the bottom; from 768px a centered form card)
 * Views live in src/views/teacherPortal/ (applicant views in src/views/teacherPortal/applicant/), building blocks in
 * src/components/teacherPortal/ (CSS classes tp-* in teacherPortal.css).
 */
type ChildOptions = { portalTab?: string; portalNoNav?: boolean; meta?: Record<string, unknown> };

function teacherChild(path: string, name: string, component: AsyncComponent, options: ChildOptions = {}): RouteConfig {
    return {
        path,
        name,
        component,
        meta: {portal: 'teacher', portalTab: options.portalTab, portalNoNav: !!options.portalNoNav, ...(options.meta || {})},
    };
}

function applicantChild(path: string, name: string, component: AsyncComponent, options: ChildOptions = {}): RouteConfig {
    return {
        path,
        name,
        component,
        meta: {portal: 'applicant', portalTab: options.portalTab, portalNoNav: !!options.portalNoNav, ...(options.meta || {})},
    };
}

const layout = () => import(/* webpackChunkName: "teacher-portal" */ '@/components/teacherPortal/PortalLayout.vue');

export const TEACHER_PORTAL_ROUTES: RouteConfig[] = [
    {
        path: '/my-teaching',
        component: layout,
        meta: {portal: 'teacher'},
        children: [
            {path: '', redirect: 'today', meta: {portal: 'teacher'}},
            teacherChild('today', 'Heute', () => import(/* webpackChunkName: "teacher-portal" */ '@/views/teacherPortal/TeacherTodayView.vue'),
                {portalTab: 'today'}),
            // Krankmeldung ("Ausfall melden"): own action area at the bottom, no bottom navigation (mockup)
            teacherChild('sick-report', 'Krankmeldung', () => import(/* webpackChunkName: "teacher-portal-absence" */ '@/views/teacherPortal/TeacherSickReportView.vue'),
                {portalTab: 'today', portalNoNav: true}),
            teacherChild('schedule', 'Mein Stundenplan', () => import(/* webpackChunkName: "teacher-portal" */ '@/views/teacherPortal/TeacherScheduleView.vue'),
                {portalTab: 'schedule'}),
            // Vertretungen & Angebote (?tab=open|confirmed|absences)
            teacherChild('substitutions', 'Vertretungen & Angebote', () => import(/* webpackChunkName: "teacher-portal-substitutions" */ '@/views/teacherPortal/TeacherSubstitutionsView.vue'),
                {portalTab: 'substitutions'}),
            teacherChild('payoff', 'Abrechnung', () => import(/* webpackChunkName: "teacher-portal-payoff" */ '@/views/teacherPortal/TeacherPayoffView.vue'),
                {portalTab: 'payoff'}),
            teacherChild('course-logs', 'Kursprotokolle', () => import(/* webpackChunkName: "teacher-portal-logs" */ '@/views/teacherPortal/TeacherCourseLogsView.vue'),
                {portalTab: 'schedule'}),
            teacherChild('profile', 'Profil & Dokumente', () => import(/* webpackChunkName: "teacher-portal" */ '@/views/teacherPortal/TeacherProfileView.vue'),
                {portalTab: 'profile'}),
            teacherChild('profile/data', 'Meine Daten', () => import(/* webpackChunkName: "teacher-portal-profile" */ '@/views/teacherPortal/TeacherProfileEditView.vue'),
                {portalTab: 'profile'}),
            // One section of „Meine Daten“ as own page (phones): save button fixed at the bottom, no bottom navigation
            teacherChild('profile/data/:section(contact|address|bank|tax|mobility)', 'Meine Daten bearbeiten',
                () => import(/* webpackChunkName: "teacher-portal-profile" */ '@/views/teacherPortal/TeacherProfileSectionView.vue'),
                {portalTab: 'profile', portalNoNav: true}),
            // Menüpunkt „Verfügbarkeiten“ (and target of the reminder „Stimmen deine Zeiten noch?“)
            teacherChild('availabilities', 'Verfügbarkeiten', () => import(/* webpackChunkName: "teacher-portal" */ '@/views/teacherPortal/TeacherAvailabilityCheckView.vue'),
                {portalTab: 'availabilities', portalNoNav: true}),
            teacherChild('availabilities/edit', 'Zeiten ändern', () => import(/* webpackChunkName: "teacher-portal-profile" */ '@/views/teacherPortal/TeacherAvailabilityEditView.vue'),
                {portalTab: 'availabilities'}),
            // Kündigungen in deinen Kursen (Zahlen der eigenen Kurse, Warnung mit QM-Schwelle)
            teacherChild('terminations', 'Kündigungen in deinen Kursen', () => import(/* webpackChunkName: "teacher-portal-terminations" */ '@/views/teacherPortal/TeacherTerminationsView.vue'),
                {portalTab: 'today'}),
            // Agent G/H (Hilfe & Weiterbildung)
            teacherChild('help', 'Hilfe & Weiterbildung', () => import(/* webpackChunkName: "teacher-portal-help" */ '@/views/teacherPortal/TeacherHelpView.vue'),
                {portalTab: 'help'}),
        ],
    },
];

export const APPLICANT_PORTAL_ROUTES: RouteConfig[] = [
    {
        path: '/my-application',
        component: layout,
        meta: {portal: 'applicant'},
        // Agent I (Bewerbungs-Inhalte) fills these views
        children: [
            applicantChild('', 'Meine Bewerbung', () => import(/* webpackChunkName: "applicant-portal" */ '@/views/teacherPortal/applicant/ApplicantStatusView.vue'),
                {portalTab: 'application'}),
            applicantChild('profile', 'Meine Daten', () => import(/* webpackChunkName: "applicant-portal" */ '@/views/teacherPortal/applicant/ApplicantProfileView.vue'),
                {portalTab: 'profile'}),
            applicantChild('trainings', 'Schulungen', () => import(/* webpackChunkName: "applicant-portal" */ '@/views/teacherPortal/applicant/ApplicantTrainingsView.vue'),
                {portalTab: 'trainings'}),
            applicantChild('contact', 'Ansprechpartner', () => import(/* webpackChunkName: "applicant-portal" */ '@/views/teacherPortal/applicant/ApplicantContactView.vue'),
                {portalTab: 'contact'}),
            // „Bewerbung zurückziehen“ und „Konto & Daten löschen“ entfallen (12.10.: nur das Büro schließt / löscht);
            // alte Links / Lesezeichen landen auf „Meine Bewerbung“
            {path: 'withdraw', redirect: '/my-application', meta: {portal: 'applicant'}},
            {path: 'delete', redirect: '/my-application', meta: {portal: 'applicant'}},
        ],
    },
];
